import { Component, OnInit, ViewChild } from '@angular/core';

import { Router, RouterLink } from '@angular/router';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import * as _ from 'lodash';
import moment from 'moment';
import {
    AppInformationService,
    AuthService,
    CdrUnitOfWork,
    RentalService,
    SessionInformationService,
    UtilityService
} from 'src/app/core/core.module';
import { GridUtility } from 'src/app/core/grid-utility';
import { Dumpster } from 'src/app/core/model/dumpster';
import { Customer, DispatchNote } from 'src/app/core/model/entity-model';
import { JobType } from 'src/app/core/model/job-type';
import { Rental } from 'src/app/core/model/rental';
import { DialogService } from 'src/app/shared/dialog.service';
import { RentalJobDialogComponent } from '../rental-job-dialog/rental-job-dialog.component';
import { DispatchNotesOverviewComponent } from 'src/app/dispatch-notes/dispatch-notes-overview/dispatch-notes-overview.component';

@Component({
    selector: 'app-rental-overview',
    templateUrl: './rental-overview.component.html',
    styleUrls: ['./rental-overview.component.scss'],
    imports: [DispatchNotesOverviewComponent, DxDataGridModule, RouterLink]
})
export class RentalOverviewComponent implements OnInit {
    dumpsters: Dumpster[];
    jobTypes: JobType[];
    rentals: Rental[];
    customers: Customer[];
    dispatchNotes: DispatchNote[];
    gridUtil: GridUtility;
    isRentalAdmin = false;
    isCustomerAdmin = false;

    @ViewChild('rentalsGrid') rentalsGrid: DxDataGridComponent;

    constructor(
        private uow: CdrUnitOfWork,
        private appInfo: AppInformationService,
        private router: Router,
        private dialogService: DialogService,
        private rentalService: RentalService,
        private sessionInfo: SessionInformationService,
        private auth: AuthService,
        private utilityService: UtilityService
    ) {}

    async ngOnInit() {
        this.appInfo.pleaseWaitShow();
        this.dumpsters = this.uow.lookups.dumpsters as Dumpster[];
        this.jobTypes = this.uow.lookups.jobTypes as JobType[];
        await this.getData();
        this.gridUtil = new GridUtility(this.rentalsGrid, 'rentals-grid', this.sessionInfo);
        this.gridUtil.setScrollPosition();
        this.appInfo.pleaseWaitHide();
        this.isRentalAdmin = this.auth.hasRole('Rentals:Admin');
        this.isCustomerAdmin = this.auth.hasRole('Customer:Admin');
    }

    async getData() {
        await this.getActiveRentalCustomers();
        await this.getRentals();
        await this.getDispatchNotes();
    }

    async getActiveRentalCustomers() {
        //get the customer for active rentals, rather than doing an expand
        this.customers = await this.uow.customers.getActiveRentalCustomers();
    }

    async getRentals() {
        this.rentals = await this.uow.rentals.getRentals('serviceAddress, status', true);

        // inventoryHtml persists when navigating away and then back to page. This is cleared to prevent duplicate inventory lines
        this.rentals.forEach(rental => ((rental as any).inventoryHtml = null));

        const rentalInventories = await this.uow.rentals.getRentalInventory();
        rentalInventories.forEach(rentalInventory => {
            const rental = this.rentals.find(rental => rental.id === rentalInventory.rentalId) as any;
            if (!rental) return;
            const onSiteDumpsters = [];
            rental.daysOnSite = 0;
            rentalInventory.data.forEach(dumpster => {
                const daysOnSite = moment().diff(moment(dumpster.placed), 'days');
                rental.daysOnSite = rental.daysOnSite < daysOnSite ? daysOnSite : rental.daysOnSite;
                onSiteDumpsters.push(`${dumpster.barcode} - ${moment(dumpster.placed).format('MM/DD/YYYY')} - ${daysOnSite} days`);
            });
            rental.inventoryHtml = onSiteDumpsters.join('<br/>');
        });
    }

    async getDispatchNotes() {
        this.dispatchNotes = await this.uow.dispatchNotes.getActiveDispatchNotes();
    }

    onRowPrepared(e) {
        if (e.rowType === 'data') {
            if (e.data.daysOnSite > 29) {
                e.rowElement.className = e.rowElement.className + ' over-30-days';
            }
            if (e.data.daysOnSite > 59) {
                e.rowElement.className = e.rowElement.className + ' over-60-days';
            }
        }
    }
    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons'
            },
            {
                template: 'toolbarText',
                location: 'after'
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    }
                }
            }
        );
    }

    onRentalRowDoubleClick(e) {
        this.openRental(e.data.id);
    }

    openRental(id?: number) {
        if (!this.isRentalAdmin) return;
        this.router.navigate([`/rentals/${id ? id : 'new'}`]);
    }

    createJob(rental: Rental, startDate: Date, jobTypeId: number) {
        return this.rentalService.createJob(rental, rental.serviceAddress, startDate, jobTypeId);
    }

    async add(rental: Rental, type: 'Delivery' | 'PickUp' | 'Exchange' | 'RoundTrip' | 'Service') {
        const jobType = JobType.getJobTypIdeByName(type);
        let job = this.createJob(rental, moment(new Date()).startOf('day').toDate(), jobType);
        job.dumpsterId = rental.dumpsterId;
        job.customerRequestedDumpsterId = rental.dumpsterId;

        const dialogRef = this.dialogService.open(RentalJobDialogComponent);
        dialogRef.instance.job = job;
        dialogRef.instance.dialogHeader = type;
        dialogRef.instance.dumpsters = this.uow.lookups.dumpsters;
        dialogRef.instance.dumpsterRequired = true;
        dialogRef.instance.showPreferredTime = type !== 'Service';

        const results = await dialogRef.instance.show();

        if (results.success) {
            job.dumpsterWeightLimit = results.value.job.billAsDumpster.weightLimit;
            if (jobType === JobType.Types.RoundTrip || jobType === JobType.Types.Exchange) {
                //make the job a delivery for RoundTrip/Exchange and the additional job the Pickup
                job.actionId = JobType.Types.Delivery;
                const additionalJob = this.createJob(rental, job.startDate, jobType);
                additionalJob.dumpsterId =
                    jobType === JobType.Types.Exchange ? results.value.job.exchangeDumpsterId : results.value.job.dumpsterId;
                additionalJob.actionId = JobType.Types.PickUp;
                additionalJob.billAsDumpsterId = results.value.job.billExchangeAs;

                const billExchangeAs = this.uow.lookups.dumpsters.find(d => d.id === results.value.job.billExchangeAs);
                additionalJob.dumpsterWeightLimit = billExchangeAs ? billExchangeAs.weightLimit : 0;
            }

            this.appInfo.pleaseWaitShow();
            await this.uow.commit();
            await this.utilityService.updateRentalStatuses([rental.id]);
            await this.getData();
            this.appInfo.pleaseWaitHide();
        } else this.uow.rollback();
        this.dialogService.close(dialogRef);
    }

    async deleteRental(rentals: Rental[]) {
        if (!(await confirm(`Are you sure you want to delete the selected rental${rentals.length > 1 ? 's' : ''}?`, this.appInfo.appTitle)))
            return;

        for (const rental of rentals) {
            rental.deleted = new Date();
            const jobs = await this.uow.jobs.getActiveJobsByRentalId(rental.id);
            for (const job of jobs) {
                job.deleted = new Date();
            }
        }

        this.appInfo.pleaseWaitShow();
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    getStatusClass(rental: Rental) {
        if (rental.statusId == null) return '';
        const status = rental.status.name;
        return status === 'New' ? 'new' : status === 'Complete' ? 'complete' : 'in-progress';
    }

    async completeRental(rentals: Rental[]) {
        if (!rentals) return;

        const success = await confirm('Are you sure you want to complete the selected rental(s)?', this.appInfo.appTitle);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        const incompleteRentals = [];
        for (const rental of rentals) {
            const jobs = await this.uow.jobs.getActiveJobsByRentalId(rental.id);
            const incompleteJobs = jobs.filter(j => !j.completed && !j.deleted);
            if (incompleteJobs.length > 0) {
                incompleteRentals.push(rental.id);
            } else rental.completed = new Date();
        }

        await this.uow.commit();
        await this.getRentals();
        this.appInfo.pleaseWaitHide();

        if (incompleteRentals.length > 0) {
            await alert(
                `These rentals cannot be completed due to active jobs.\r\n${incompleteRentals.join('\r\n')}`,
                this.appInfo.appTitle
            );
        }
    }

    async markComplete(dispatchNote: DispatchNote) {
        if (!dispatchNote) return;

        this.appInfo.pleaseWaitShow();
        dispatchNote.completed = moment(new Date()).toDate();
        await this.uow.commit();
        await this.getDispatchNotes();
        this.appInfo.pleaseWaitHide();
    }

    open(rental: Rental, dataField: string) {
        switch (dataField) {
            case 'id':
                this.openRental(rental.id);
                break;
            case 'customer.displayName':
                if (!this.isCustomerAdmin) break;
                this.router.navigate([`/customers/${rental.customerId}`]);
                break;
            case 'siteAddressHTML':
                this.router.navigate([`/jobs/sites/${rental.serviceAddressId}`]);
                break;
        }
    }

    statusSortValue = (rental: Rental) => {
        if (rental.status == null) return null;
        const status = rental.status.name;
        return status === 'Complete' ? 0 : status === 'In Progress' ? 1 : 2;
    };

    statusCellValue = (rental: Rental) => {
        if (rental.status == null) return null;
        return rental.status.name;
    };
}
